<template>
  <div
    v-click-outside="closePopover"
    v-focus-outside="closePopover"
    class="button-popover"
    @mouseenter="trigger === 'hover' ? togglePopover() : ''"
    @mouseleave="trigger === 'hover' ? togglePopover() : ''"
  >
    <ButtonClassic
      :class="{ 'open': isPopoverVisible, }"
      :variant="variant"
      :color="color"
      :label="label"
      :disabled="disabled"
      :size="size"
      :icon="icon"
      @click="togglePopover()"
    >
      <template #left-icon>
        <!-- @slot Emplacement pour une icône à gauche du label. -->
        <slot name="left-icon" />
      </template>
      <template
        v-if="$slots.label"
        #label
      >
        <!--
          @slot Emplacement pour un label dans le cas où le prop "label" de base n'est pas défini.
          Cela permet par exemple d'ajouter du contenu HTML.
        -->
        <slot name="label" />
      </template>
      <template #right-icon>
        <!-- @slot Emplacement pour une icône à droite du label. -->
        <slot name="right-icon" />
      </template>
    </ButtonClassic>
    <div
      v-if="isPopoverVisible"
      class="popover-container"
      :class="placement"
    >
      <!-- @slot Emplacement pour le contenu. -->
      <slot name="default" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ButtonClassic from "../../components/buttons/ButtonClassic.vue";

/**
 * Affiche un encadré au clic sur un bouton prédéfini, qui contient des éléments interactifs ou
 * des informations supplémentaires.
 */
export default defineComponent({
  name: "ButtonPopover",
  components: {
    ButtonClassic,
  },
  props: {
    /**
     * Définit si le popover est ouvert ou non.
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Texte dans le bouton.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Définit une apparence alternative (bordure, fond...).
     */
    variant: {
      type: String,
      default: "simple",
      validator: (value) => ["simple", "ghost", "solid", "special"].includes(value),
    },
    /**
     * Spécifie un ensemble de couleurs.
     * "black" n'est utilisable qu'avec la variante "special".
     */
    color: {
      type: String,
      default: "white",
      validator: (value) => ["white", "black", "primary", "secondary"].includes(value),
    },
    /**
     * Affiche une icône dans le bouton à un emplacement donné.
     */
    icon: {
      type: String,
      default: "none",
      validator: (value) => ["left", "right", "both", "none"].includes(value),
    },
    /**
     * Taille globale.
     */
    size: {
      type: String,
      default: "medium",
      validator: (value) => ["small", "medium", "large"].includes(value),
    },
    /**
     * Désactive le bouton.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Spécifie les déclencheurs qui afficheront le popover.
     */
    trigger: {
      type: String,
      validator: (value) => ["click", "hover"].includes(value),
      default: "hover",
    },
    /**
     * Placement du popover dans la page.
     */
    placement: {
      type: String,
      validator: (value) => [
        "top", "bottom", "right", "left", "top-right", "top-left", "bottom-right", "bottom-left",
      ].includes(value),
      default: "bottom",
    },
  },
  emits: [
    /**
     * Déclenché lorsque l'état du popover change.
     * @param {Boolean} bool État du popover.
     */
    "update:modelValue",
  ],
  computed: {
    isPopoverVisible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
  },
  methods: {
    /**
     * Affiche le popover s'il est fermé. Ferme le popover s'il est ouvert.
     */
    togglePopover() {
      this.isPopoverVisible = !this.isPopoverVisible;
    },
    /**
     * Ferme le popover.
     */
    closePopover() {
      this.isPopoverVisible = false;
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/buttons/button_popover.scss";
</style>
